import { useEffect, useState } from 'react'
import { asText, clear, entries, subscribe, type Entry } from '../lib/debuglog'
import { VERSION, COMMIT } from '../lib/version'

/**
 * The debug log, for admins.
 *
 * Gated on a capability rather than an environment: the phone that needs
 * diagnosing is always a real one in production, so a panel that only exists in
 * development is a panel that is never there when it is wanted.
 */
export default function DebugPanel({ environment }: { environment: string }) {
  const [open, setOpen] = useState(false)
  const [, force] = useState(0)
  const [copied, setCopied] = useState(false)

  useEffect(() => subscribe(() => force((n) => n + 1)), [])

  if (!open) {
    return (
      <button className="debug-tab" onClick={() => setOpen(true)} title="Debug log">
        ⌘ {entries().length}
      </button>
    )
  }

  const list = [...entries()].reverse()

  return (
    <div className="debug-panel" role="dialog" aria-label="Debug log">
      <header>
        <div>
          <strong>Debug log</strong>
          <span className="muted small mono"> {VERSION} · {COMMIT} · {environment}</span>
        </div>
        <span className="row">
          <button onClick={async () => {
            try {
              await navigator.clipboard.writeText(asText())
              setCopied(true)
              setTimeout(() => setCopied(false), 1500)
            } catch {
              // Clipboard is permission-gated and refuses outside a gesture in
              // some browsers. Selecting the text by hand still works.
            }
          }}>{copied ? 'Copied' : 'Copy'}</button>
          <button onClick={clear}>Clear</button>
          <button onClick={() => setOpen(false)}>Close</button>
        </span>
      </header>

      <ol className="debug-lines">
        {list.length === 0 && <li className="muted">Nothing yet.</li>}
        {list.map((e: Entry, i) => (
          <li key={`${e.at}-${i}`} className={`lvl-${e.level}`}>
            <span className="ts">{new Date(e.at).toISOString().slice(11, 19)}</span>
            <span className="lvl">{e.level}</span>
            <span className="msg">{e.message}</span>
          </li>
        ))}
      </ol>

      <p className="muted small">
        Stays on this device. Nothing is uploaded, and no request or response bodies are recorded.
      </p>
    </div>
  )
}
